import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, Warehouse, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { StockPlanningItem, WizardDelivery } from '../../../lib/api';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { toast } from 'sonner';
import LocalColumnManager from './LocalColumnManager';
import { useCommodityTypeOptions } from './useCommodityTypeOptions';
import { DEFAULT_WAREHOUSE_COLUMNS, type LocalColumn } from './DirectDeliveryColumns';
import { getStockHealthColor, getItemCellValue, useLocalColumns } from './warehouseWizardGrid';
import { useTableSort } from '../../../hooks/useTableSort';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';

export interface WizardWarehouse {
  id: number;
  name: string;
  code: string;
  warehouse_type: string;
}

interface WarehouseDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (delivery: WizardDelivery) => void;
  supplierId: number;
  existingFillPercent: number;
  editDelivery?: WizardDelivery;
  warehouses: WizardWarehouse[];
  /** CR-6293: open-PO availability window (days), lifted to the wizard so it
   *  persists on the call-off. Defaults to the stock-planning value. */
  openPoDays: number;
  setOpenPoDays: (days: number) => void;
  /** The wizard's pickup date (YYYY-MM-DD); a later pickup extends the window. */
  pickupDate?: string;
}

export default function WarehouseDeliveryModal({
  open, onClose, onAdd, supplierId, existingFillPercent, editDelivery, warehouses,
  openPoDays, setOpenPoDays, pickupDate,
}: WarehouseDeliveryModalProps) {
  // Empty string = no selection. The user must explicitly pick a warehouse;
  // this prevents accidentally saving against the first id in the list.
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [stockItems, setStockItems] = useState<StockPlanningItem[]>([]);
  const [loading, setLoading] = useState(false);
  // Client-side row filters. "hideTestParts" mirrors the supplier-side Current
  // Stock filter (hide rows whose part number/description starts with "part").
  // "commodityFilter" narrows to a single commodity type ("all" = no filter).
  const [hideTestParts, setHideTestParts] = useState(false);
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  // Qty already committed for this delivery line at edit-mode entry. The
  // backend's PO-availability number already counts this against committed,
  // so we add it back to "effectiveAvailable" — otherwise editing a saved
  // line would always look over-limit.
  const [originalQty, setOriginalQty] = useState<Record<number, number>>({});
  const hasPrefilledRef = useRef(false);
  const { config } = useCallOffConfig();

  // Column management. The "sum_on_saltney" column label is overridden at
  // render time with the customer's primary_warehouse_column_label so the
  // same column ID renders different copy per tenant without invalidating
  // stored column preferences.
  const { columns: rawColumns, visible: rawVisibleCols, toggle: toggleCol, moveUp: moveColUp } = useLocalColumns(DEFAULT_WAREHOUSE_COLUMNS);
  const applyConfigLabels = useCallback((cols: LocalColumn[]) => cols.map((c) =>
    c.id === 'sum_on_saltney'
      ? { ...c, label: config.primary_warehouse_column_label || c.label }
      : c
  ), [config.primary_warehouse_column_label]);
  const columns = useMemo(() => applyConfigLabels(rawColumns), [rawColumns, applyConfigLabels]);
  const visibleCols = useMemo(() => applyConfigLabels(rawVisibleCols), [rawVisibleCols, applyConfigLabels]);

  const fetchStockPlanning = useCallback(async () => {
    setLoading(true);
    try {
      // Page through every part for this supplier. A single page_size=500 fetch
      // silently dropped parts for suppliers with more than 500 assignments
      // (e.g. DREWAR has ~950), so the delivery grid was missing rows. Loop
      // until we've collected total_count items.
      const PAGE_SIZE = 500;
      const MAX_PAGES = 50; // safety stop (~25k parts) against a bad total_count
      // CR-6293: pass the call-off's window so Available To Call Off reflects
      // PO due within max(today + openPoDays, pickupDate).
      const windowParams: Record<string, string | number> = { open_po_days: openPoDays };
      if (pickupDate) windowParams['pickup_date'] = pickupDate;
      const first = await callOffAPI.getStockPlanning({
        supplier_id: supplierId,
        page_size: PAGE_SIZE,
        page: 1,
        ...windowParams,
      });
      let items = first.data.items || [];
      const total = first.data.total_count || items.length;
      let page = 2;
      while (items.length < total && page <= MAX_PAGES) {
        const res = await callOffAPI.getStockPlanning({
          supplier_id: supplierId,
          page_size: PAGE_SIZE,
          page,
          ...windowParams,
        });
        const batch = res.data.items || [];
        if (batch.length === 0) break;
        items = items.concat(batch);
        page += 1;
      }
      setStockItems(items);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load stock planning data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [supplierId, openPoDays, pickupDate]);

  useEffect(() => {
    if (open) { hasPrefilledRef.current = false; }
  }, [open]);

  useEffect(() => {
    if (open) { fetchStockPlanning(); }
  }, [open, fetchStockPlanning]);

  // Pre-fill quantities when editing
  useEffect(() => {
    if (!open || hasPrefilledRef.current || stockItems.length === 0) return;
    if (editDelivery) {
      const qtyMap: Record<number, number> = {};
      const pnToStockId = new Map<string, number>();
      for (const item of stockItems) {
        pnToStockId.set(item.part_number, item.id);
      }
      for (const part of editDelivery.parts) {
        if (part.partId !== null && stockItems.some(s => s.id === part.partId)) {
          qtyMap[part.partId] = part.qty;
        } else {
          const stockId = pnToStockId.get(part.partNumber);
          if (stockId !== undefined) qtyMap[stockId] = part.qty;
        }
      }
      setQuantities(qtyMap);
      setOriginalQty(qtyMap);
      // Only prefill the dropdown if the existing destination is still a
      // valid active warehouse for this customer. If it's stale (e.g. an old
      // soft-deleted warehouse), leave it empty so the user is forced to
      // re-pick — no silent rewriting to a default.
      const valid = editDelivery.destinationId != null
        && warehouses.some(w => w.id === editDelivery.destinationId);
      setSelectedWarehouse(valid ? String(editDelivery.destinationId) : '');
      hasPrefilledRef.current = true;
    } else {
      setQuantities({});
      setOriginalQty({});
      hasPrefilledRef.current = true;
    }
  }, [open, editDelivery, stockItems]);

  const handleQtyChange = (itemId: number, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setQuantities(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    } else {
      setQuantities(prev => ({ ...prev, [itemId]: numValue }));
    }
  };

  // Commodity types carried by the loaded parts. Merged with the configured
  // list in the hook below, so the dropdown offers every commodity type rather
  // than only those that happen to appear on screen.
  const commodityTypesInRows = useMemo(() => {
    const set = new Set<string>();
    for (const it of stockItems) {
      if (it.commodity_type) set.add(it.commodity_type);
    }
    return Array.from(set);
  }, [stockItems]);

  const commodityOptions = useCommodityTypeOptions(commodityTypesInRows);

  // Rows after applying the hide-"part" and commodity-type filters.
  const visibleItems = useMemo(
    () => stockItems.filter(item => {
      if (
        hideTestParts &&
        ((item.part_number || '').toLowerCase().startsWith('part') ||
          (item.description || '').toLowerCase().startsWith('part'))
      ) return false;
      if (commodityFilter !== 'all' && (item.commodity_type || '') !== commodityFilter) return false;
      return true;
    }),
    [stockItems, hideTestParts, commodityFilter]
  );

  // CR-6296: spreadsheet-style sort of the delivery grid. Double-click a column
  // (e.g. Stock Health Min) to float negative/deficit parts to the top so they
  // get called off first. Client-side over every loaded part. Row keys are the
  // stable item.id, so reordering never loses a typed Qty.
  const {
    sortColumn, sortDirection, sortedItems: sortedVisibleItems,
    handleHeaderClick, handleHeaderDoubleClick, getAriaSort,
  } = useTableSort<StockPlanningItem>(visibleItems);

  const renderSortIcon = (colId: string) => {
    if (sortColumn !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50 shrink-0" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary shrink-0" />
      : <ArrowDown className="h-3 w-3 text-primary shrink-0" />;
  };

  const selectedItems = useMemo(
    () => stockItems.filter(item => quantities[item.id] > 0),
    [stockItems, quantities]
  );

  const modalFillPercent = useMemo(() => {
    let fill = 0;
    for (const item of selectedItems) {
      const qty = quantities[item.id] || 0;
      const loadQty = parseFloat(item.load_qty) || 0;
      if (loadQty > 0) fill += qty / loadQty;
    }
    return Math.round(fill * 100);
  }, [selectedItems, quantities]);

  const totalFillPercent = existingFillPercent + modalFillPercent;
  const totalUnits = selectedItems.reduce((sum, item) => sum + (quantities[item.id] || 0), 0);

  // Per-item over-limit check. Backend availability already excludes our own
  // committed qty for *other* call-offs, so we add back what this very line
  // contributed before edit so we don't double-count it against ourselves.
  const overInfo = useMemo(() => {
    const map: Record<number, { over: boolean; effective: number }> = {};
    for (const item of stockItems) {
      const raw = parseFloat(item.available_to_call_off as unknown as string) || 0;
      const own = originalQty[item.id] || 0;
      const effective = raw + own;
      const qty = quantities[item.id] || 0;
      map[item.id] = { over: qty > 0 && qty > effective, effective };
    }
    return map;
  }, [stockItems, quantities, originalQty]);

  const overCount = selectedItems.filter(i => overInfo[i.id]?.over).length;
  const hasOverLimit = overCount > 0;

  const handleAdd = () => {
    const warehouse = warehouses.find(w => String(w.id) === selectedWarehouse);
    if (!warehouse) { toast.error('Please select a warehouse'); return; }
    if (selectedItems.length === 0) { toast.error('Please enter quantities for at least one item'); return; }
    // A quantity with no PO behind it cannot be called off. The backend rejects
    // it outright, so stop here with a readable message rather than letting the
    // wizard build a delivery the save will refuse.
    if (hasOverLimit) {
      toast.error(
        `${overCount} item${overCount !== 1 ? 's' : ''} exceed the available PO quantity. ` +
        'Reduce the quantity to continue.'
      );
      return;
    }

    const delivery: WizardDelivery = {
      type: 'warehouse',
      destinationId: warehouse.id,
      destinationName: warehouse.name,
      salesOrderId: null,
      address: '',
      postcode: '',
      deliveryDate: '',
      parts: selectedItems.map(item => ({
        partId: item.id,
        partNumber: item.part_number,
        description: item.description,
        qty: quantities[item.id] || 0,
        palletQty: parseFloat(item.pallet_qty) || 0,
        loadQty: parseFloat(item.load_qty) || 0,
      })),
    };
    onAdd(delivery);
  };

  const handleClose = () => {
    setQuantities({});
    hasPrefilledRef.current = false;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col p-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <Warehouse className="h-5 w-5 text-red-500" />
          <h2 className="text-lg font-semibold">
            {editDelivery ? 'Edit Warehouse Delivery' : 'Add Warehouse Delivery'}
          </h2>
        </div>

        {/* Toolbar */}
        <div className="flex items-end gap-4 px-6 py-3 border-b bg-muted/30">
          <div className="space-y-1">
            <Label className="text-xs">
              Warehouse <span className="text-red-500">*</span>
            </Label>
            <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
              <SelectTrigger className={`w-48 ${!selectedWarehouse ? 'border-red-300' : ''}`}>
                <SelectValue placeholder={
                  warehouses.length === 0 ? 'Loading…' : 'Select warehouse'
                } />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map(w => (
                  <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <LocalColumnManager columns={columns} onToggle={toggleCol} onMoveUp={moveColUp} />
          <div className="space-y-1">
            <Label className="text-xs">Parts</Label>
            <select
              value={hideTestParts ? 'hide_part' : 'all'}
              onChange={(e) => setHideTestParts(e.target.value === 'hide_part')}
              className="w-44 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Row filter"
            >
              <option value="all">Show all parts</option>
              <option value="hide_part">Hide &ldquo;part&rdquo;-prefixed</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Commodity Type</Label>
            <select
              value={commodityFilter}
              onChange={(e) => setCommodityFilter(e.target.value)}
              className="w-44 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Commodity type filter"
            >
              <option value="all">All commodity types</option>
              {commodityOptions.map(ct => (
                <option key={ct} value={ct}>{ct}</option>
              ))}
            </select>
          </div>
          {/* CR-6293: open-PO window. Available To Call Off counts PO due within
              max(today + this, pickup date). Defaults to the stock-planning value. */}
          <div className="space-y-1">
            <Label className="text-xs">Open PO Days</Label>
            <input
              type="number"
              min={0}
              value={openPoDays}
              onChange={(e) => setOpenPoDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
              className="w-24 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Open PO days window"
              title="PO due within this many days counts as available. A later pickup date extends the window."
            />
          </div>
          <div className="flex-1" />
          <div className="text-right space-y-1">
            <Label className="text-xs">Container Fill</Label>
            <div className="flex items-center gap-2">
              <Progress value={Math.min(totalFillPercent, 100)} className="w-32 h-2" />
              <span className={`text-sm font-bold ${totalFillPercent > 90 ? 'text-red-600' : totalFillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                {totalFillPercent}%
              </span>
            </div>
            {!editDelivery && existingFillPercent > 0 && (
              <p className="text-xs text-muted-foreground">
                Existing: {existingFillPercent}% + This: {modalFillPercent}%
              </p>
            )}
          </div>
        </div>

        {/* Stock grid */}
        <div className="flex-1 overflow-auto px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : stockItems.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              No stock planning data found for this supplier.
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground text-sm">
              All {stockItems.length} parts were hidden by the active filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20">
                <tr className="text-left text-xs font-semibold text-foreground bg-muted border-b shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]">
                  {visibleCols.map(col => {
                    const rightAlign = col.id !== 'part_number' && col.id !== 'description';
                    return (
                      <th
                        key={col.id}
                        onClick={() => handleHeaderClick(col.id)}
                        onDoubleClick={() => handleHeaderDoubleClick(col.id)}
                        title="Click to sort • double-click to bring negative values to the top"
                        aria-sort={getAriaSort(col.id)}
                        className={`py-2.5 px-2 whitespace-normal break-words align-top min-w-[80px] cursor-pointer select-none hover:bg-muted-foreground/10 ${
                          sortColumn === col.id ? 'bg-muted-foreground/10' : ''
                        } ${rightAlign ? 'text-right' : ''}`}
                      >
                        <div className={`flex items-start gap-1 ${rightAlign ? 'justify-end' : 'justify-between'}`}>
                          <span>{col.label}</span>
                          {renderSortIcon(col.id)}
                        </div>
                      </th>
                    );
                  })}
                  <th className="py-2.5 px-2 text-right w-24 bg-yellow-100 text-yellow-900 align-top">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortedVisibleItems.map(item => (
                  <tr
                    key={item.id}
                    className={`hover:bg-muted/50 ${getStockHealthColor(item.stock_health_min, item.stock_health_max)} ${quantities[item.id] > 0 ? 'bg-blue-50' : ''}`}
                  >
                    {visibleCols.map(col => (
                      <td key={col.id} className={`py-1.5 px-2 align-top whitespace-normal break-words ${
                        col.id === 'part_number' ? 'font-mono text-xs' :
                        col.id === 'description' ? 'text-xs max-w-[200px]' :
                        'text-right'
                      }`}>
                        {getItemCellValue(item, col.id)}
                      </td>
                    ))}
                    <td className={`py-1.5 px-2 ${overInfo[item.id]?.over ? 'bg-red-50' : 'bg-yellow-50'}`}>
                      <Input
                        type="number"
                        min={0}
                        className={`h-7 w-20 text-right text-sm ${
                          overInfo[item.id]?.over
                            ? 'border-red-500 bg-red-50 text-red-700 focus-visible:ring-red-500'
                            : ''
                        }`}
                        value={quantities[item.id] || ''}
                        onChange={(e) => handleQtyChange(item.id, e.target.value)}
                        placeholder="0"
                      />
                      {overInfo[item.id]?.over && (
                        <div className="text-[10px] text-red-700 mt-0.5 whitespace-nowrap">
                          {overInfo[item.id].effective === 0
                            ? 'No PO available'
                            : `Only ${overInfo[item.id].effective} available`}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'} selected &middot;{' '}
            {totalUnits} units
          </div>
          <div className="flex items-center gap-2">
            {hasOverLimit && (
              <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1">
                {overCount} item{overCount !== 1 ? 's' : ''} exceed available qty — reduce to continue
              </span>
            )}
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleAdd}
              disabled={selectedItems.length === 0 || !selectedWarehouse || hasOverLimit}
              title={
                !selectedWarehouse
                  ? 'Select a warehouse first'
                  : hasOverLimit
                    ? 'One or more quantities exceed the available PO quantity'
                    : undefined
              }
            >
              {editDelivery ? 'Update Delivery' : 'Add Delivery to Call-Off'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
